import {
  ActionIcon,
  type ComboboxItem,
  type ComboboxItemGroup,
  Group,
  Select
} from "@mantine/core";
import { IconDeviceFloppy, IconTrash } from "@tabler/icons-react";
import { useCallback, useMemo, useState } from "react";
import type { Meter, Site } from "../../../../utils/backend-types";
import type { SelectedMeter } from "../../EditConfigurationModal/EditConfigurationModal";
import type { MeterConfigurationData } from "../../ThirdPartySystems.types";
import { getSuggestedMeterDisplayName } from "../../utils/getSuggestedMeterDisplayName";
import "./MeterSelectionCell.scss";

interface MeterSelectionProps {
  meterParameter: MeterConfigurationData;
  selectedMeters: Array<SelectedMeter>;
  metersBySiteId: Array<Meter> | undefined;
  sites: Site[];
  onChange: (value: string) => void;
  onMeterSave: (singleEntityId?: number) => Promise<{ success: boolean }>;
  onMeterRemove: (meterId: number) => Promise<void>;
}

function MeterSelectionCell({
  meterParameter,
  selectedMeters,
  metersBySiteId,
  sites,
  onChange,
  onMeterSave,
  onMeterRemove
}: MeterSelectionProps) {
  const [isSavingMeter, setIsSavingMeter] = useState<boolean>(false);
  const [isDeletingMeter, setIsDeletingMeter] = useState<boolean>(false);

  const meterData: Array<ComboboxItemGroup<ComboboxItem<string>>> = useMemo(
    () =>
      sites.map((site) => {
        const relevantMeters =
          metersBySiteId?.filter(
            (meter) => meter.number && meter.site === site.id
          ) || [];

        return {
          group: site.name,
          items: relevantMeters.map((meter) => ({
            label:
              meterParameter.suggestedMeter === meter.id
                ? getSuggestedMeterDisplayName(meter)
                : `${meter.number} | ${meter.name}`,
            value: meter.id.toString()
          }))
        };
      }),
    [meterParameter, metersBySiteId, sites]
  );

  const handleChangeValue = useCallback(
    (value: string | null) => {
      onChange(value || "");
    },
    [onChange]
  );

  const savedMeter = metersBySiteId?.find(
    (meter) => meter.id === meterParameter.meter
  );

  const savedMeterDisplayName = savedMeter?.number
    ? `${savedMeter.number} | ` + savedMeter?.name
    : savedMeter?.name;

  return !meterParameter.meter ? (
    <Group className="MeterSelectionCell" gap="xs" wrap="nowrap">
      <Select
        clearable
        comboboxProps={{ width: 300 }}
        data={meterData}
        defaultValue={
          selectedMeters
            .find(
              (meter) => meter.subMeteringSystemEntityId === meterParameter.id
            )
            ?.meterId.toString() ?? undefined
        }
        id={meterParameter.id.toString()}
        name={meterParameter.id.toString()}
        required
        onChange={handleChangeValue}
      />

      {selectedMeters.find(
        (meter) =>
          meter.subMeteringSystemEntityId === meterParameter.id &&
          meter.meterName
      ) && (
        <ActionIcon
          loading={isSavingMeter}
          size="xs"
          variant="transparent"
          onClick={async () => {
            setIsSavingMeter(true);
            await onMeterSave(meterParameter.id);
            setIsSavingMeter(false);
          }}
        >
          <IconDeviceFloppy title="Zähler speichern" />
        </ActionIcon>
      )}
    </Group>
  ) : (
    <div className="MeterSwitchWrapper">
      <p className="SavedMeterText" data-for={`${savedMeter?.id}`} data-tip>
        {savedMeterDisplayName}
      </p>

      <ActionIcon
        c="error"
        loading={isDeletingMeter}
        size="xs"
        variant="transparent"
        onClick={async () => {
          setIsDeletingMeter(true);
          await onMeterRemove(meterParameter.id);
          setIsDeletingMeter(false);
        }}
      >
        <IconTrash />
      </ActionIcon>
    </div>
  );
}

export { MeterSelectionCell };
